import { FolderPlus, Layers, Maximize2 } from "lucide-react";
import { useState } from "react";

import { ErrorState, LoadingState } from "@/components/feedback";
import { Button } from "@/components/ui/button";
import { Tip } from "@/components/ui/overlays";
import { workspace } from "@/components/workspace/controller";
import { CATEGORY_LABEL, type NoteCategory } from "@/lib/api";
import { cn } from "@/lib/utils";

import { FileContextMenu } from "./FileContextMenu";
import { FileTree } from "./FileTree";
import { createFolder } from "./file-actions";
import { useFileSelection } from "./file-selection";
import { useFileManager } from "./use-file-manager";

/**
 * The left rail's file explorer.
 *
 * Structurally this is the same `FileTree` Folder View renders, over the same
 * `/api/folders` tree — which is the point. The rail used to mirror the DISK
 * (`Papers/` + `Markdown/`) while every other surface showed the four product
 * categories, so a handwritten note appeared under "Papers" here and under
 * "Handwritten" everywhere else, and dragging in one place taught you nothing
 * about the other. It also carried its own single-selection model, its own
 * drag payload, and its own copy of the open-a-file routing.
 *
 * All of that now comes from `useFileManager`, so the rail gains multi-select,
 * multi-file drag, cut/paste, rename, delete and folder colours for free — and
 * cannot drift from Folder View again.
 */
export function ExplorerPane({ embedded = false }: { embedded?: boolean } = {}) {
  // Shortcuts are owned by whichever file surface the user is actually working
  // in. The rail is a companion to a document most of the time, so it does not
  // claim Delete/F2/Ctrl+A globally; Folder View does.
  const manager = useFileManager({ enableShortcuts: false });
  // A null category creates a PROJECT folder — one that exists in every
  // category at once, so "Research" can gather notes, handwriting and papers.
  const [creating, setCreating] = useState<{
    category: NoteCategory | null;
    parent: string;
  } | null>(null);

  if (manager.tree.isLoading) {
    return <LoadingState variant="skeleton" rows={6} className="p-2" />;
  }
  if (manager.tree.isError) {
    return (
      <ErrorState
        error={manager.tree.error}
        onRetry={() => void manager.tree.refetch()}
        label="files"
        className="p-3"
      />
    );
  }

  return (
    // `w-full min-w-0` is load-bearing, not decoration: without a width
    // constraint this column sizes to its widest row, so the tree grew to ~650px
    // inside a 250px rail — long filenames were hard-clipped by the rail's
    // `overflow-hidden` instead of truncating with an ellipsis, and anything
    // right-aligned in a header row landed off-screen entirely.
    <div className={cn("flex min-h-0 w-full min-w-0 flex-1 flex-col", embedded ? "" : "w-full")}>
      <div className="flex h-7 shrink-0 items-center gap-1 px-1.5">
        <span className="flex-1 truncate font-mono text-[9px] uppercase tracking-widest text-muted-foreground/70">
          Vault
        </span>
        <Tip label="New folder — appears in every category" side="right">
          <button
            type="button"
            aria-label="New folder"
            onClick={() => setCreating({ category: null, parent: "" })}
            className="rounded p-0.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <Layers className="h-3 w-3" />
          </button>
        </Tip>
      </div>
      <div className="min-h-0 w-full min-w-0 flex-1 overflow-y-auto px-1 pb-2">
        <FileTree
          roots={manager.roots}
          shared={manager.tree.data?.shared ?? []}
          defaultOpenDepth={1}
          callbacks={{
            onOpen: manager.openItem,
            onDropInto: manager.dropInto,
            onFolderContextMenu: (folder, at) =>
              manager.setMenu({ kind: "folder", folder, ...at }),
            onItemContextMenu: (refs, at) => manager.setMenu({ kind: "files", refs, ...at }),
            folderActions: (folder) => (
              <Tip label={`New folder inside “${folder.name}” — folders can nest`} side="right">
                <button
                  type="button"
                  aria-label={`New folder inside ${folder.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCreating({ category: folder.category, parent: folder.path });
                  }}
                  className="rounded p-0.5 text-muted-foreground opacity-0 transition-opacity hover:bg-accent hover:text-foreground focus-visible:opacity-100 group-hover/folder:opacity-100"
                >
                  <FolderPlus className="h-3 w-3" />
                </button>
              </Tip>
            ),
          }}
        />
        {creating && (
          <input
            autoFocus
            placeholder={`New folder in ${
              creating.parent ||
              (creating.category ? CATEGORY_LABEL[creating.category] : "every category")
            }…`}
            onBlur={() => setCreating(null)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setCreating(null);
              if (e.key === "Enter") {
                const value = e.currentTarget.value.trim();
                setCreating(null);
                if (value) {
                  void createFolder(
                    creating.category,
                    creating.parent ? `${creating.parent}/${value}` : value,
                  );
                }
              }
            }}
            className="mx-1 mt-1 w-[calc(100%-8px)] rounded border border-border bg-input px-2 py-1 text-[11.5px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          />
        )}
      </div>

      {/* The rail is narrow; anything that needs room says so and hands over. */}
      <button
        type="button"
        onClick={() => workspace.openFiles()}
        className="flex shrink-0 items-center gap-1.5 border-t border-sidebar-border/50 px-2.5 py-1.5 text-left text-[11.5px] text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
      >
        <Maximize2 className="h-3 w-3" /> Open Folder View
      </button>

      <FileContextMenu
        state={manager.menu}
        onClose={() => manager.setMenu(null)}
        canPaste={!!manager.clipboard}
        onRenameFolder={manager.folderActions.rename}
        onNewSubfolder={(folder) =>
          setCreating({ category: folder.category, parent: folder.path })
        }
        onSetColor={manager.folderActions.setColor}
        onDeleteFolder={manager.folderActions.remove}
        onPaste={manager.paste}
        onRenameFile={(ref) => manager.setEditing({ kind: "rename-file", ref })}
        onDeleteFiles={(refs) => manager.setConfirmDelete(refs)}
        onCut={(refs) => useFileSelection.getState().cut(refs)}
        onCopy={(refs) => useFileSelection.getState().copy(refs)}
        onDuplicate={manager.duplicate}
      />

      {manager.editing?.kind === "rename-file" && (
        <RailRename
          title={manager.editing.ref.title}
          onCommit={manager.commitEditing}
          onCancel={() => manager.setEditing(null)}
        />
      )}
      {manager.editing?.kind === "rename-folder" && (
        <RailRename
          title={manager.editing.folder.name}
          onCommit={manager.commitEditing}
          onCancel={() => manager.setEditing(null)}
        />
      )}
      {manager.confirmDelete && (
        <RailConfirm
          count={manager.confirmDelete.length}
          title={manager.confirmDelete[0]?.title ?? ""}
          onCancel={() => manager.setConfirmDelete(null)}
          onConfirm={() => void manager.deleteSelected(manager.confirmDelete!)}
        />
      )}
    </div>
  );
}

function RailRename({
  title,
  onCommit,
  onCancel,
}: {
  title: string;
  onCommit: (value: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(title);
  return (
    <div className="fixed inset-0 z-[90] grid place-items-center bg-black/50" onClick={onCancel}>
      <div
        className="w-[320px] rounded-lg bg-popover p-4 shadow-[0_24px_64px_-24px_rgba(0,0,0,0.9)]"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="pb-2 font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
          Rename
        </p>
        <input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onFocus={(e) => e.currentTarget.select()}
          onKeyDown={(e) => {
            if (e.key === "Enter") onCommit(value);
            if (e.key === "Escape") onCancel();
          }}
          className="w-full rounded border border-border bg-input px-2 py-1.5 text-[13px] text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
        />
        <div className="mt-3 flex justify-end gap-1.5">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="sm" onClick={() => onCommit(value)}>
            Rename
          </Button>
        </div>
      </div>
    </div>
  );
}

function RailConfirm({
  count,
  title,
  onConfirm,
  onCancel,
}: {
  count: number;
  title: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[90] grid place-items-center bg-black/50" onClick={onCancel}>
      <div
        className="w-[360px] rounded-lg bg-popover p-4 shadow-[0_24px_64px_-24px_rgba(0,0,0,0.9)]"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="text-[13px] leading-relaxed text-foreground/90">
          {count === 1 ? `Delete “${title}”?` : `Delete ${count} files?`}
        </p>
        <p className="pt-1 text-[12px] text-muted-foreground">
          This removes them from the atlas and your vault.
        </p>
        <div className="mt-3 flex justify-end gap-1.5">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            size="sm"
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={onConfirm}
          >
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
}
